import React, { useEffect, useRef } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { MdDelete } from "react-icons/md";
import {
  deleteOtherInvestor,
  getAllOtherInvestor,
} from "./services/otherInvestor.services";
import {
  clearOtherInvestorDetails,
  setInvestmentGUID,
} from "./otherInvestorSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AlertBox from "../../components/common/AlertBox";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { checkAcessRights } from "../../_helper/helper";

const OtherInvestor: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  const LeadID = location?.state?.otherinverstor?.LeadID;
  useEffect(() => {
    dispatch(getAllOtherInvestor(LeadID));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleOtherInvestorCreate = () => {
    navigate("/otherinvestors/create", {
      state: {
        LeadID: LeadID,
      },
    });
  };
  return (
    <>
      <DeleteOtherInvestorDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Other Investment List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleOtherInvestorCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Create Other Investment
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <OtherInvestorGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const DeleteOtherInvestorDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const LeadID = location?.state?.otherinverstor?.LeadID;
  const InvestmentGUID = useAppSelector(
    (state) => state.otherinvestor?.InvestmentGUID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: string) => {
    if (ID) {
      const response = await dispatch(deleteOtherInvestor(ID));
      if (response.payload?.Data.Status === 200) {
        dispatch(getAllOtherInvestor(LeadID));
        dispatch(closeDialog());
        dispatch(clearOtherInvestorDetails());
      } else {
        dispatch(getAllOtherInvestor(LeadID));
        dispatch(closeDialog());
        dispatch(clearOtherInvestorDetails());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteOtherInvestor" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Other Investment?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Other Investment?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(InvestmentGUID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const OtherInvestorGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const OtherInvestorList = useAppSelector(
    (state) => state.otherinvestor.OtherInvestorList
  );
  const loading = useAppSelector((state) => state.otherinvestor.loading);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const LeadID = location?.state?.otherinverstor?.LeadID;
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? OtherInvestorList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEditItem = (InvestmentGUID: string, LeadID: number) => {
      navigate("/otherinvestors/edit", {
        state: { InvestmentGUID, LeadID },
      });
    };
    const handleOpenDeleteDialog = (ID: string) => {
      dispatch(openDialog("deleteOtherInvestor"));
      dispatch(setInvestmentGUID(ID));
    };
    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() =>
              handleEditItem(props.dataItem.InvestmentGUID, LeadID)
            }
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
themeColor="error"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem.InvestmentGUID)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  //   const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
  //     const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

  //     const handleActiveInActive = () => {
  //       setIsActive(!IsActive);
  //       const data = {
  //         ID: props.dataItem.ID,
  //         IsActive: !IsActive,
  //       };
  //       dispatch(OtherInvestorActiveInactive(data)).then((response: any) => {
  //         if (response.payload?.data?.Status === 200) {
  //           dispatch(getAllOtherInvestor());
  //         } else {
  //           dispatch(getAllOtherInvestor());
  //         }
  //       });
  //     };

  //     return (
  //       <td style={{ textAlign: "center" }}>
  //         <Switch
  //           name="IsActive"
  //           checked={IsActive}
  //           disabled={isEditAccess ? false : true}
  //           onChange={handleActiveInActive}
  //           size="small"
  //         />
  //       </td>
  //     );
  //   };

  return (
    <>
      {OtherInvestorList && OtherInvestorList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
 className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(OtherInvestorList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : OtherInvestorList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={OtherInvestorList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="LeadName"
            title="Client Name"
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/otherinvestors/edit", {
                        state: {
                          InvestmentGUID: props.dataItem?.InvestmentGUID,
                        },
                      })
                    }
                  >
                    {props.dataItem?.LeadName}
                  </div>
                ) : (
                  props.dataItem?.LeadName
                )}
              </td>
            )}
          />
          <Column field="CategoryName" title="Category" />
          <Column field="AllottedDate" title="Allotted Date" />
          <Column field="UserName" title="User Name" />
          <Column field="InvestmentDetails" title="Investment Details" />
          <Column field="FurtherAction" title="Further Action" />
          {/* <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          /> */}
          {(isDeleteAccess || isEditAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

export default OtherInvestor;
